<script lang="ts">
  import { onMount } from "svelte";
  import { app } from "../../state/app.svelte";
  import { getLastSilentOutcome, silentLogin, type SilentOutcome } from "../../api/auth";
  import {
    buildBundle,
    triggerBundleDownload,
    parseBundle,
    importBundle,
    type ImportSummary,
  } from "../../api/dataBundle";
  import { i18n } from "../../i18n/i18n.svelte";

  const fr = {
    title: "À propos & confidentialité",
    storageTitle: "Où sont stockées vos données ?",
    parseTitle: "Serveur EPFL — parsing des documents",
    loggedTitle: "Ce qui est enregistré dans les logs serveur :",
    neverLogged: "Jamais loggé :",
    llmTitle: "Service LLM (RCP EPFL) — embeddings et chat",
    sentToRcp: "Données envoyées à RCP via le serveur :",
    authTitle: "Authentification — Microsoft Entra (EPFL)",
    silentLabel: "Dernier renouvellement automatique de la session :",
    silentOk: "réussi",
    silentFailed: "échec",
    silentNever: "jamais tenté",
    silentTest: "Tester maintenant",
    silentTesting: "Test en cours…",
    transferTitle: "Transférer vos données (extension ↔ page web)",
    exporting: "Export en cours…",
    exportBtn: "⬇ Exporter mes données",
    importing: "Import en cours…",
    importBtn: "⬆ Importer un fichier",
    reloadHint: "Rechargez l'application pour voir les données importées.",
    reload: "Recharger l'application",
    deleteTitle: "Comment supprimer vos données ?",
    wipeBtn: "Tout effacer…",
    wiping: "Effacement…",
    wipeYes: "Oui, tout effacer",
    cancel: "Annuler",
  };
  const en: typeof fr = {
    title: "About & privacy",
    storageTitle: "Where is your data stored?",
    parseTitle: "EPFL server — document parsing",
    loggedTitle: "What the server logs record:",
    neverLogged: "Never logged:",
    llmTitle: "LLM service (EPFL RCP) — embeddings and chat",
    sentToRcp: "Data sent to RCP through the server:",
    authTitle: "Authentication — Microsoft Entra (EPFL)",
    silentLabel: "Last automatic session renewal:",
    silentOk: "succeeded",
    silentFailed: "failed",
    silentNever: "never attempted",
    silentTest: "Test now",
    silentTesting: "Testing…",
    transferTitle: "Transfer your data (extension ↔ web page)",
    exporting: "Exporting…",
    exportBtn: "⬇ Export my data",
    importing: "Importing…",
    importBtn: "⬆ Import a file",
    reloadHint: "Reload the app to see the imported data.",
    reload: "Reload the app",
    deleteTitle: "How do I delete my data?",
    wipeBtn: "Erase everything…",
    wiping: "Erasing…",
    wipeYes: "Yes, erase everything",
    cancel: "Cancel",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  const version = __APP_VERSION__;
  const platform = __PLATFORM__;
  const serverUrl = $derived(app.config?.serverUrl ?? "—");
  const rcpUrl = $derived(app.config?.rcpUrl ?? "—");
  const storage = $derived(app.storage);

  // Why the session did (not) renew itself on its own — the clue to a daily
  // re-login. Read once when the panel opens.
  let silent = $state<SilentOutcome | null>(null);
  onMount(() => {
    void getLastSilentOutcome()
      .then((o) => (silent = o))
      .catch(() => {});
  });

  // Same renewal as the one tried when the refresh token dies, on demand: a
  // failure never logs the user out (silentLogin only writes on success), and
  // the outcome lands in the line above within seconds instead of tomorrow.
  let testingSilent = $state(false);
  async function testSilent() {
    testingSilent = true;
    try {
      await silentLogin();
    } finally {
      silent = await getLastSilentOutcome().catch(() => null);
      testingSilent = false;
    }
  }

  let confirmingWipe = $state(false);
  let wiping = $state(false);

  async function wipeAll() {
    wiping = true;
    try {
      await app.wipeAllData(); // reloads the page on success
    } catch (err) {
      wiping = false;
      confirmingWipe = false;
      console.error("wipe failed", err);
    }
  }

  // ---- transfert extension ↔ web -----------------------------------------
  let exporting = $state(false);
  let importing = $state(false);
  let transferError = $state<string | null>(null);
  let importSummary = $state<ImportSummary | null>(null);
  let importFileInput: HTMLInputElement;

  async function onExport() {
    transferError = null;
    importSummary = null;
    exporting = true;
    try {
      const result = await buildBundle();
      triggerBundleDownload(result);
    } catch (err) {
      transferError = err instanceof Error ? err.message : String(err);
    } finally {
      exporting = false;
    }
  }

  async function onImportFile(e: Event) {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    target.value = ""; // allow re-importing the same file
    if (!file) return;
    transferError = null;
    importSummary = null;
    importing = true;
    try {
      const bundle = await parseBundle(file);
      importSummary = await importBundle(bundle);
    } catch (err) {
      transferError = err instanceof Error ? err.message : String(err);
    } finally {
      importing = false;
    }
  }

  function reloadApp() {
    location.reload();
  }
</script>

<div class="about">
  <h2>{t.title}</h2>
  {#if i18n.locale === "en"}
    <p class="muted">Personal RAG v{version} — EPFL DPO agent. This page
      describes exactly which data goes where.</p>
  {:else}
    <p class="muted">Personal RAG v{version} — agent EPFL DPO. Cette page décrit
      précisément quelles données circulent où.</p>
  {/if}

  <section>
    <h3>{t.storageTitle}</h3>
    {#if i18n.locale === "en"}
      <p>
        <strong>Everything stays local in your browser</strong>, in the
        extension's IndexedDB. Nothing is synced to a cloud, nothing is shared
        with other users.
      </p>
      <ul>
        <li>Imported documents (original file)</li>
        <li>Parsed pages and text chunks</li>
        <li>Vector embeddings of the chunks</li>
        <li>Conversations and messages (questions, answers, citations)</li>
        <li>Settings (server URL, models)</li>
        <li>EPFL OIDC session (id_token + refresh_token, encrypted by the browser)</li>
      </ul>
    {:else}
      <p>
        <strong>Tout reste localement dans votre navigateur</strong>, dans
        l'IndexedDB de l'extension. Rien n'est synchronisé sur un cloud, rien
        n'est partagé avec d'autres utilisateurs.
      </p>
      <ul>
        <li>Documents importés (binaire original)</li>
        <li>Pages parsées et chunks de texte</li>
        <li>Embeddings vectoriels des chunks</li>
        <li>Conversations et messages (questions, réponses, citations)</li>
        <li>Configuration (URL serveur, modèles)</li>
        <li>Session EPFL OIDC (id_token + refresh_token chiffrés par le navigateur)</li>
      </ul>
    {/if}
    {#if storage}
      <p
        class="storage-status"
        class:ok={storage.persisted}
        class:warn={!storage.persisted}
      >
        {#if i18n.locale === "en"}
          {#if storage.persisted}
            ✓ <strong>Persistent storage granted</strong> — your data will not
            be cleared automatically by the browser.
          {:else if storage.supported}
            ⚠ <strong>Persistent storage not granted</strong> — depending on
            the browser, your data may be cleared after a period of inactivity
            (Safari: ~7 days). Export your important conversations as a
            precaution.
          {:else}
            ⚠ <strong>This browser does not support persistent storage</strong> —
            your data may be cleared when storage runs low. Export your
            important conversations as a precaution.
          {/if}
        {:else}
          {#if storage.persisted}
            ✓ <strong>Stockage persistant accordé</strong> — vos données ne seront
            pas effacées automatiquement par le navigateur.
          {:else if storage.supported}
            ⚠ <strong>Stockage persistant non accordé</strong> — selon le
            navigateur, vos données peuvent être effacées après une période
            d'inactivité (Safari : ~7 jours). Exportez vos conversations
            importantes par précaution.
          {:else}
            ⚠ <strong>Ce navigateur ne gère pas le stockage persistant</strong> —
            vos données peuvent être effacées sous pression de stockage. Exportez
            vos conversations importantes par précaution.
          {/if}
        {/if}
      </p>
    {/if}
  </section>

  <section>
    <h3>{t.parseTitle}</h3>
    <p class="endpoint"><code>POST {serverUrl}/parse</code></p>
    {#if i18n.locale === "en"}
      <p>
        Each time you import a document, the file is sent to the server to be
        split into pages and chunks. The server returns the result and then
        forgets it: <strong>no document is kept after the response.</strong>
      </p>
    {:else}
      <p>
        À chaque import de document, le fichier binaire est envoyé au serveur
        pour être découpé en pages et chunks. Le serveur renvoie le résultat
        puis l'oublie : <strong>aucun document n'est conservé après la
        réponse.</strong>
      </p>
    {/if}
    <p><strong>{t.loggedTitle}</strong></p>
    {#if i18n.locale === "en"}
      <ul>
        <li>User identifier (EPFL OIDC sub)</li>
        <li>SHA-256 hash of the document, size in bytes</li>
        <li>Estimated token count, number of chunks produced</li>
        <li>Parser used, OCR yes/no, latency</li>
      </ul>
      <p>
        <strong>{t.neverLogged}</strong> file name, document content, parsed
        text, chunks.
      </p>
    {:else}
      <ul>
        <li>Identifiant utilisateur (sub OIDC EPFL)</li>
        <li>Hash SHA-256 du document, taille en octets</li>
        <li>Estimation du nombre de tokens, nombre de chunks produits</li>
        <li>Parser utilisé, OCR oui/non, latence</li>
      </ul>
      <p>
        <strong>{t.neverLogged}</strong> nom du fichier, contenu du document,
        texte parsé, chunks.
      </p>
    {/if}
  </section>

  <section>
    <h3>{t.llmTitle}</h3>
    <p class="endpoint">
      Extension → <code>{serverUrl}/llm/*</code> → <code>{rcpUrl}</code>
    </p>
    {#if i18n.locale === "en"}
      <p>
        All LLM calls (embeddings, question rewriting, chat)
        <strong>go through the EPFL server</strong>, which forwards them to RCP
        using a shared application key. The browser never has access to this
        key. Your requests are authenticated by your OIDC session.
      </p>
      <p><strong>{t.sentToRcp}</strong></p>
      <ul>
        <li>Embeddings: text of the chunks and of your question</li>
        <li>Chat: system prompt + rewritten question + retrieved chunks
          (the cited passages) + short conversation history</li>
      </ul>
      <p><strong>{t.loggedTitle}</strong></p>
      <ul>
        <li>User identifier (OIDC sub), model used</li>
        <li>Token count (input and output), latency</li>
      </ul>
      <p>
        <strong>{t.neverLogged}</strong> content of questions, content of
        answers, content of chunks, embeddings.
      </p>
    {:else}
      <p>
        Tous les appels LLM (embeddings, reformulation de question, chat)
        <strong>passent par le serveur EPFL</strong>, qui les
        reforwarde à RCP en utilisant une clé d'application partagée. Le
        navigateur n'a jamais accès à cette clé. Vos requêtes sont
        authentifiées par votre session OIDC.
      </p>
      <p><strong>{t.sentToRcp}</strong></p>
      <ul>
        <li>Embeddings : texte des chunks et de votre question</li>
        <li>Chat : prompt système + question reformulée + chunks récupérés
          (les passages cités) + historique court de la conversation</li>
      </ul>
      <p><strong>{t.loggedTitle}</strong></p>
      <ul>
        <li>Identifiant utilisateur (sub OIDC), modèle utilisé</li>
        <li>Nombre de tokens (entrée et sortie), latence</li>
      </ul>
      <p>
        <strong>{t.neverLogged}</strong> contenu des questions, contenu des
        réponses, contenu des chunks, embeddings.
      </p>
    {/if}
  </section>

  <section>
    <h3>{t.authTitle}</h3>
    <p class="endpoint"><code>login.microsoftonline.com</code></p>
    {#if i18n.locale === "en"}
      <p>
        Sign-in uses the PKCE flow: your EPFL password is entered directly at
        Microsoft and is never sent to the extension or to the Personal RAG
        server. The extension only receives tokens (id_token and
        refresh_token) that prove your identity to the server.
      </p>
    {:else}
      <p>
        Le login utilise le flux PKCE : votre mot de passe EPFL est saisi
        directement chez Microsoft, jamais transmis à l'extension ni au serveur
        Personal RAG. L'extension ne reçoit que des tokens (id_token et
        refresh_token) qui prouvent votre identité au serveur.
      </p>
    {/if}
    <p>
      {t.silentLabel}
      {#if silent}
        <strong>{silent.ok ? t.silentOk : t.silentFailed}</strong>
        ({new Date(silent.at).toLocaleString(i18n.locale === "en" ? "en-GB" : "fr-CH")})
        {#if !silent.ok}<br /><code>{silent.reason}</code>{/if}
      {:else}
        {t.silentNever}
      {/if}
    </p>
    <button onclick={testSilent} disabled={testingSilent}>
      {testingSilent ? t.silentTesting : t.silentTest}
    </button>
  </section>

  <section>
    <h3>{t.transferTitle}</h3>
    {#if i18n.locale === "en"}
      <p>
        The extension and the web page are two applications <strong>kept
        apart by the browser</strong>: each has its own storage, which cannot
        be merged automatically. To find on one side what you indexed on the
        other, export a file and then import it — <strong>nothing goes through
        a server</strong>, the file stays under your control.
      </p>
      <p class="muted">
        The file contains documents, embeddings, folders, conversations and
        settings. It <strong>never includes your EPFL session</strong>
        (authentication tokens) — you simply sign in again on the other side.
      </p>
    {:else}
      <p>
        L'extension et la page web sont deux applications <strong>séparées par le
        navigateur</strong> : chacune a son propre stockage, impossible à fusionner
        automatiquement. Pour retrouver d'un côté ce que vous avez indexé de
        l'autre, exportez un fichier puis importez-le — <strong>rien ne passe par
        un serveur</strong>, le fichier reste sous votre contrôle.
      </p>
      <p class="muted">
        Le fichier contient documents, embeddings, dossiers, conversations et
        réglages. Il <strong>n'inclut jamais votre session EPFL</strong> (tokens
        d'authentification) — vous vous reconnectez normalement de l'autre côté.
      </p>
    {/if}

    <div class="transfer-actions">
      <button onclick={onExport} disabled={exporting}>
        {exporting ? t.exporting : t.exportBtn}
      </button>
      <button onclick={() => importFileInput.click()} disabled={importing}>
        {importing ? t.importing : t.importBtn}
      </button>
      <input
        bind:this={importFileInput}
        type="file"
        accept="application/json,.json"
        class="hidden-file"
        onchange={onImportFile}
      />
    </div>

    <p class="muted transfer-hint">
      {#if i18n.locale === "en"}
        {#if platform === "web"}
          Tip: export from the extension, then import the file here.
        {:else}
          Tip: export here, then import the file on the web page
          <code>personal-rag.epfl.ch</code> (About menu).
        {/if}
      {:else}
        {#if platform === "web"}
          Astuce : exportez depuis l'extension, puis importez le fichier ici.
        {:else}
          Astuce : exportez ici, puis importez le fichier sur la page web
          <code>personal-rag.epfl.ch</code> (menu À&nbsp;propos).
        {/if}
      {/if}
    </p>

    {#if transferError}
      <p class="transfer-msg error">{transferError}</p>
    {/if}

    {#if importSummary}
      <div class="transfer-msg ok">
        {#if i18n.locale === "en"}
          <p>
            ✓ Import complete:
            <strong>{importSummary.documentsAdded}</strong> document{importSummary.documentsAdded === 1 ? "" : "s"} added
            {#if importSummary.documentsSkipped > 0}
              ({importSummary.documentsSkipped} already present){/if},
            <strong>{importSummary.conversationsAdded}</strong> conversation{importSummary.conversationsAdded === 1 ? "" : "s"},
            <strong>{importSummary.messagesAdded}</strong> message{importSummary.messagesAdded === 1 ? "" : "s"}.
          </p>
        {:else}
          <p>
            ✓ Import terminé :
            <strong>{importSummary.documentsAdded}</strong> document{importSummary.documentsAdded > 1 ? "s" : ""} ajouté{importSummary.documentsAdded > 1 ? "s" : ""}
            {#if importSummary.documentsSkipped > 0}
              ({importSummary.documentsSkipped} déjà présent{importSummary.documentsSkipped > 1 ? "s" : ""}){/if},
            <strong>{importSummary.conversationsAdded}</strong> conversation{importSummary.conversationsAdded > 1 ? "s" : ""},
            <strong>{importSummary.messagesAdded}</strong> message{importSummary.messagesAdded > 1 ? "s" : ""}.
          </p>
        {/if}
        <p>{t.reloadHint}</p>
        <button onclick={reloadApp}>{t.reload}</button>
      </div>
    {/if}
  </section>

  <section>
    <h3>{t.deleteTitle}</h3>
    {#if i18n.locale === "en"}
      <p><strong>Delete one or more documents:</strong></p>
      <ul>
        <li>
          Open the <strong>Docs</strong> tab, tick the documents to delete,
          then click <em>Delete selection</em>. The <code>×</code> button on
          each document also deletes it individually.
        </li>
        <li>
          Deleting removes the file, its chunks and its embeddings.
          Conversations that referred to these documents stay in the history
          (their citations simply become non-clickable).
        </li>
      </ul>
      <p>
        <strong>Sign out of EPFL</strong> without erasing everything: use the
        <em>Sign out</em> button in Settings.
      </p>
      <p>
        <strong>Erase everything</strong> (documents, conversations,
        embeddings, settings, OIDC session), directly from the app:
      </p>
    {:else}
      <p><strong>Supprimer un ou plusieurs documents :</strong></p>
      <ul>
        <li>
          Ouvrez l'onglet <strong>Docs</strong>, cochez les documents à
          supprimer, puis cliquez sur <em>Supprimer la sélection</em>. Le
          bouton <code>×</code> sur chaque document permet aussi une
          suppression individuelle.
        </li>
        <li>
          La suppression efface le binaire, les chunks et les embeddings
          associés. Les conversations qui référençaient ces documents restent
          dans l'historique (les citations deviennent simplement non
          cliquables).
        </li>
      </ul>
      <p>
        <strong>Vous déconnecter d'EPFL</strong> sans tout effacer&nbsp;:
        utilisez le bouton <em>Se déconnecter</em> dans la configuration.
      </p>
      <p>
        <strong>Tout effacer</strong> (documents, conversations, embeddings,
        configuration, session OIDC), directement depuis l'application&nbsp;:
      </p>
    {/if}
    {#if !confirmingWipe}
      <button class="danger" onclick={() => (confirmingWipe = true)}>
        {t.wipeBtn}
      </button>
    {:else}
      <div class="wipe-confirm">
        {#if i18n.locale === "en"}
          <p>
            <strong>Permanently</strong> erase all local data? This cannot be
            undone.
          </p>
        {:else}
          <p>
            Effacer <strong>définitivement</strong> toutes les données locales&nbsp;?
            Cette action est irréversible.
          </p>
        {/if}
        <div class="wipe-actions">
          <button class="danger" disabled={wiping} onclick={wipeAll}>
            {wiping ? t.wiping : t.wipeYes}
          </button>
          <button disabled={wiping} onclick={() => (confirmingWipe = false)}>
            {t.cancel}
          </button>
        </div>
      </div>
    {/if}
  </section>
</div>

<style>
  .about {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    line-height: 1.45;
    font-size: 0.85rem;
    color: var(--epfl-text);
  }

  h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }

  h3 {
    margin: 0 0 0.4rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--epfl-text);
  }

  section {
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    padding: 0.7rem 0.85rem;
    background: var(--epfl-white);
  }

  section p {
    margin: 0 0 0.5rem;
  }

  section p:last-child {
    margin-bottom: 0;
  }

  ul {
    margin: 0.3rem 0 0.6rem;
    padding-left: 1.2rem;
  }

  li {
    margin: 0.15rem 0;
  }

  code {
    font-family: var(--epfl-font-mono);
    font-size: 0.78rem;
    background: var(--epfl-gray-50);
    padding: 0.05rem 0.3rem;
    border-radius: var(--epfl-radius);
    word-break: break-all;
  }

  .endpoint {
    color: var(--epfl-gray-600);
    font-size: 0.8rem;
  }

  .muted {
    color: var(--epfl-gray-400);
    margin: 0;
  }

  .storage-status {
    margin: 0.5rem 0 0;
    padding: 0.45rem 0.6rem;
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
  }

  .storage-status.ok {
    background: var(--epfl-success-bg);
    color: var(--epfl-success-text);
    border: 1px solid var(--epfl-success-border);
  }

  .storage-status.warn {
    background: var(--epfl-warning-bg);
    color: var(--epfl-warning-text);
    border: 1px solid var(--epfl-warning-border);
  }

  button {
    font: inherit;
    padding: 0.35rem 0.7rem;
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    background: var(--epfl-white);
    color: var(--epfl-text);
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.6;
    cursor: default;
  }

  button.danger {
    border-color: var(--epfl-danger);
    background: var(--epfl-danger);
    color: var(--epfl-white);
  }

  .wipe-confirm {
    border: 1px solid var(--epfl-danger-border);
    background: var(--epfl-danger-bg);
    border-radius: var(--epfl-radius);
    padding: 0.6rem 0.7rem;
  }

  .wipe-confirm p {
    margin: 0 0 0.5rem;
  }

  .wipe-actions {
    display: flex;
    gap: 0.5rem;
  }

  .transfer-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 0.6rem 0 0.4rem;
  }

  .hidden-file {
    display: none;
  }

  .transfer-hint {
    font-size: 0.78rem;
  }

  .transfer-msg {
    margin: 0.5rem 0 0;
    padding: 0.5rem 0.65rem;
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
  }

  .transfer-msg.ok {
    background: var(--epfl-success-bg);
    color: var(--epfl-success-text);
    border: 1px solid var(--epfl-success-border);
  }

  .transfer-msg.ok p {
    margin: 0 0 0.4rem;
  }

  .transfer-msg.ok button {
    border-color: var(--epfl-success-text);
    color: var(--epfl-success-text);
  }

  .transfer-msg.error {
    background: var(--epfl-danger-bg);
    color: var(--epfl-danger-text);
    border: 1px solid var(--epfl-danger-border);
  }

</style>
